import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"
import { openToPath } from "./workspaceNoteSlice"
import {ensureWorkspaceTemplate} from "../utils/reduxUtilities"

export const fetchLastDeletedNote = createAsyncThunk(
	"undoDeleteNoteSlice/fetchLastDeletedNote",
	async (workspaceId, {rejectWithValue}) => {

		if (!workspaceId) return rejectWithValue("No valid workspace id.")

		try {
			const { data: lastDeletedNote } = await axios.get(`/api/workspace/${workspaceId}/note-deleted`)
			return lastDeletedNote
		} catch (error) {
			return rejectWithValue(error?.response?.message || "Error fetching last deleted note")
		}
	}
)

export const undoDeleteNote = createAsyncThunk(
	"undoDeleteNoteSlice/undoDeleteNote",
	async (deletedNote, { rejectWithValue, dispatch}) => {
		const { workspaceId } = deletedNote
		if (!workspaceId) return rejectWithValue("No valid workspace id.")

		try {
			const {data: relayPath} = await axios.put(`/api/workspace/${workspaceId}/note-deleted/${deletedNote.id}`, {
				...deletedNote,
			})
			// if parent is in tree and no children, fetch children
			dispatch(openToPath({path: deletedNote.path, note: deletedNote, insertTarget: true}))
			
			return {relayPath, deletedNote}
		} catch(error) {
			return rejectWithValue(error?.response?.message || "Error undoing the last deleted note. Please try again later.")

		}
	}
)
const initialState = {
	deletedNote: null,
	isLoading: false,
	fetchUndoNoteError: null,
	undoNoteError: null,
	successMessage: null,
	isModalOpen: false
}

const ensureWorkspace = (state, workspaceId) => ensureWorkspaceTemplate(state, workspaceId, initialState)

export const undoDeleteNoteSlice = createSlice({
	name: "undoDeleteNoteSlice",
	initialState: {
		byWorkspace: {}
	},
	reducers: {
		setIsModalOpen: (state, action) => {
			const {isOpen, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			if (!isOpen) {
				workspace.deletedNote = null
			}
			workspace.isModalOpen = action.payload
		},
		clearFetchNoteError: (state, action) => {
			const workspaceId = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.fetchUndoNoteError = null
		},
		clearUndoNoteError: (state, action) => {
			const workspaceId = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.undoNoteError = null
		},
		clearSuccessMessage: (state, action) => {
			const workspaceId = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.successMessage = null
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchLastDeletedNote.pending, (state, action) => {
				const workspaceId = (action.meta.arg)
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.isLoading = true
			})
			.addCase(fetchLastDeletedNote.fulfilled, (state, action) => {
				const {workspaceId} = action.payload
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.deletedNote = action.payload
				workspace.isLoading = false
				workspace.isModalOpen = true
			})
			.addCase(fetchLastDeletedNote.rejected, (state, action) => {
				const workspaceId = (action.meta.arg)
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.isLoading = false
				workspace.fetchUndoNoteError = action.payload
			})
			.addCase(undoDeleteNote.pending, (state, action) => {
				const {workspaceId} = action.meta.arg
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.isLoading = true
			})
			.addCase(undoDeleteNote.fulfilled, (state, action) => {
				const {workspaceId} = action.payload.deletedNote
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.deletedNote = null
				workspace.successMessage = "Your note has been restored"
				workspace.isLoading = false
				workspace.isModalOpen = false
			})
			.addCase(undoDeleteNote.rejected, (state, action) => {
				// error handling is returned from thunk itself, here we just handle the loading state.
				const {workspaceId} = action.meta.arg
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.undoNoteError = action.payload
				workspace.isLoading = false
			})
	}
}) 
export const { setIsModalOpen, clearFetchNoteError, clearUndoNoteError, clearSuccessMessage } = undoDeleteNoteSlice.actions

// Selector to get the notes object from the state
export const selectDeletedNote = (state, workspaceId) => state.undoDeleteNoteSlice.byWorkspace[workspaceId]?.deletedNote ?? initialState.deletedNote
export const selectIsModalOpen = (state, workspaceId) => state.undoDeleteNoteSlice.byWorkspace[workspaceId]?.isModalOpen ?? initialState.isModalOpen
export const selectIsLoading = (state, workspaceId) => state.undoDeleteNoteSlice.byWorkspace[workspaceId]?.isLoading ?? initialState.isLoading
export const selectFetchUndoNoteError = (state, workspaceId) => state.undoDeleteNoteSlice.byWorkspace[workspaceId]?.fetchUndoNoteError ?? initialState.fetchUndoNoteError
export const selectUndoNoteError = (state, workspaceId) => state.undoDeleteNoteSlice.byWorkspace[workspaceId]?.undoNoteError ?? initialState.undoNoteError
export const selectSuccessMessage = (state, workspaceId) => state.undoDeleteNoteSlice.byWorkspace[workspaceId]?.successMessage ?? initialState.successMessage

export default undoDeleteNoteSlice.reducer